/**
 * GA4 type-safe wrapper with ad blocker resilience.
 * All calls are no-ops if gtag.js is blocked or not loaded.
 */

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

let initialized = false;

function gtag(...args: unknown[]): void {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;
  window.gtag(...args);
}

/**
 * Initialize GA4. Guards against double initialization.
 * Warns in development if the measurement ID is missing.
 */
export function initGA(): void {
  if (initialized) return;

  if (!GA_MEASUREMENT_ID) {
    if (import.meta.env.DEV) {
      console.warn('[analytics] VITE_GA_MEASUREMENT_ID is not set — GA4 will not track.');
    }
    return;
  }

  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
  initialized = true;
}

/**
 * Track a virtual page view (for SPA navigation).
 */
export function trackPageView(path: string, title?: string): void {
  if (!GA_MEASUREMENT_ID) return;

  gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: typeof window !== 'undefined' ? window.location.origin + path : undefined,
  });
}

/**
 * Track a custom event with optional parameters.
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
): void {
  if (!GA_MEASUREMENT_ID) return;
  gtag('event', eventName, params);
}

/**
 * Set or clear the user ID for cross-device tracking.
 * Pass `null` on sign-out.
 */
export function setUserId(userId: string | null): void {
  if (!GA_MEASUREMENT_ID) return;
  gtag('config', GA_MEASUREMENT_ID, { user_id: userId });
}

/**
 * Set custom user properties for audience segmentation.
 */
export function setUserProperties(
  properties: Record<string, string | number | boolean>,
): void {
  if (!GA_MEASUREMENT_ID) return;
  gtag('set', 'user_properties', properties);
}
